import { observable, action, computed, runInAction } from "mobx";
import { persist } from "mobx-persist";
import React from "react";

import toastService from "../../services/ToastService";
import {
  notApplicableItems,
  CUBE_QUERY_WIZARD_SQL_QUERY_PART,
} from "../../constants/constants-container";
import {
  getDateFormat,
  formatDate,
  getFilterItemKind,
  generateShortID,
  findCubeStructure,
  findCubeLinks,
  sanitizeSqlAlias,
  sanitizeForDataName,
  padWithZeros,
} from "./cubeQueryWizardUtils";
import {
  getTablesFromDataSetDistributions,
  getTablesFromDatasetUrl,
} from "./dataSetUtils";

import { CubeQueryWizardMemberRemovalWarning } from "../../components/detail/CubeQueryWizard/CubeQueryWizardMemberRemovalWarning";

/**
 * Sometimes there are two or more relationship paths from one cube to another.
 * If this constant is set to false, we ignore ON conditions from other relationships, we keep only the first one.
 *
 * @type boolean
 */
const ON_CONDITIONS_FROM_DIFFERENT_RELATIONSHIPS_IN_SAME_JOIN = false;

/**
 * Cube Relationships can define - based on mandatoriness - what kind of join is used (see code in SQL()).
 * This constant turns it off.
 *
 * @type boolean
 */
const CUBE_RELATIONSHIPS_ONLY_USE_LEFT_JOIN = true;

export default class CubeQueryWizardStore {
  @observable
  showQueryWizard = false;

  // Coming from CubeQueryWizard component @TODO - will be changed
  @observable cubeRoot = {};
  @observable cubeDetails = [{}];
  @observable dataSetDistributions = [];
  @observable cubeLinks = {}; // map of cubeId -> cubeLink of cubeRelationships
  @observable cubeStructureItemLinksByCubeId = {}; // map of cubeId -> cubeStructureItemLinks
  @observable cubeRelationships = {}; // map of cubeId -> cubeLink of cubeRelationships
  @observable foreignCubes = {}; // map of cubeId -> cube
  @observable hasCombinations = false;
  @observable mappedNonReferenceCubes = [];

  // dataSource translates to FROM in SQL
  @observable dataSource = "NO_DATA_SOURCE";
  @observable selectedForeignDataSources = {}; // map of cubeId -> data source value for foreign cube

  // Keep translates to SELECT in SQL
  @observable keepOrder = []; // order of items in keep
  @observable keepState = {}; // state of items in keep

  // Filter translates to WHERE in SQL
  @observable filterOrder = []; // order of items in filter
  @observable filterState = {}; // state of items in filter
  @observable filterOperators = []; // array of "AND"s and "OR"s that connect items in filter
  @observable filterOperatorStart = "";
  @observable filterOperatorEnd = "";

  @observable allowedValuesForVariableIds = {}; // may contain members, members from subdomains and variable
  @observable allAllowedVariableIds = [];

  // Template Representation
  @observable hasTemplateRepresentation = false;

  /**
   * If true, there are no regular ordinates on Z axis, but members.
   */
  @observable hasMembersInZAxis = false;

  @observable hasOpenYAxis = false;

  @observable selectAllForZAxis = false;

  // keeping reference between combinations and ordinates
  @observable cubeXYZ = observable.array([
    {
      byCombination: {},
      byOrdinates: {},
    },
  ]);
  @observable isTemplateRepresentationSelectionOn = false;
  @observable templateRepresentationCells = {};
  @observable templateRepresentationSelectedColumnsAndRows = {}; // per Z and X/Y
  @observable templateRepresentationSelectedAll = {}; // per Z axis ordinate
  @observable whereTRItemsContainsItems = false;
  allowedCombinationIds = new Set([]);

  selectedMember = [[]];

  // SERIES KEYS FILTER
  @observable seriesKeysFilter = "";

  // Cube Links
  @observable selectedCubeLinks = [];

  // Cube Relationships
  @observable selectedCubeRelationships = [];

  // LIMIT in SQL
  @observable limit = 50;

  @observable loadingCombinations = false;

  @persist @observable openFromCubeLinkPage = false;
  @persist("list") @observable defaultFiltersFromMemberLinks = [];

  constructor(http, detailViewStore) {
    this.http = http;
    this.detailViewStore = detailViewStore;
  }

  @action
  initializeData(
    cubeRootItem,
    cubeDetails,
    dataSetDistributions,
    hasCombinations,
    otherInformation
  ) {
    this.cubeRoot = cubeRootItem;
    this.cubeDetails = cubeDetails;
    this.dataSetDistributions = dataSetDistributions;
    this.hasCombinations = hasCombinations;

    this.mappedNonReferenceCubes =
      otherInformation &&
      otherInformation.mappedNonReferenceCubes &&
      otherInformation.mappedNonReferenceCubes.length
        ? otherInformation.mappedNonReferenceCubes
        : [];
    if (cubeRootItem.length > 0)
      for (let cubeRoot of cubeRootItem) {
        this.cubeLinks = { ...this.cubeLinks, ...findCubeLinks(cubeRoot) }; // cubeRelationships are fully loaded in this.loadCubeLinksOrRelationships("CubeRelationship")
      }
    else this.cubeLinks = findCubeLinks(cubeRootItem);
    // post-actions:
    this.loadCubeLinksOrRelationships("CubeLink");
    this.loadCubeLinksOrRelationships("CubeRelationship");
    const dataSources = this.dataSources;
    if (dataSources.length === 1) {
      this.dataSource = dataSources[0].table;
    }
  }

  /**
   * Action to reset the QueryWizard state to the initial values (above)
   */
  @action
  resetState = () => {
    this.showQueryWizard = false;

    this.cubeRoot = {};
    this.cubeDetails = [{}];
    this.dataSetDistributions = [];
    this.cubeLinks = {};
    this.cubeStructureItemLinksByCubeId = {};
    this.cubeRelationships = {};
    this.foreignCubes = {};
    this.hasCombinations = false;

    this.mappedNonReferenceCubes = [];

    this.dataSource = "NO_DATA_SOURCE";
    this.selectedForeignDataSources = {};

    this.keepOrder = []; // order of items in keep
    this.keepState = {}; // state of items in keep

    this.filterOrder = []; // order of items in filter
    this.filterState = {}; // state of items in filter
    this.filterOperators = []; // array of "AND"s and "OR"s that connect items in filter
    this.filterOperatorStart = "";
    this.filterOperatorEnd = "";

    this.allowedValuesForVariableIds = {};
    this.allAllowedVariableIds = [];

    this.hasTemplateRepresentation = false;
    this.hasMembersInZAxis = false;
    this.hasOpenYAxis = false;
    this.selectedMember = [[]];

    this.selectAllForZAxis = false;

    this.cubeXYZ = [
      {
        byCombination: {},
        byOrdinates: {},
      },
    ];
    this.isTemplateRepresentationSelectionOn = false;
    this.templateRepresentationCells = {};
    this.templateRepresentationSelectedColumnsAndRows = {};
    this.templateRepresentationSelectedAll = {};

    this.allowedCombinationIds = new Set([]);

    this.seriesKeysFilter = "";

    this.selectedCubeLinks = [];
    this.selectedCubeRelationships = [];
    this.limit = 50; // LIMIT in SQL

    this.loadingCombinations = false;

    this.openFromCubeLinkPage = false;
    this.defaultFiltersFromMemberLinks = [];
  };
  @action
  resetStateForTemplateHierarchie = () => {
    this.showQueryWizard = false;

    this.cubeRoot = {};
    this.cubeDetails = [{}];
    this.dataSetDistributions = [];
    this.cubeLinks = {};
    this.cubeStructureItemLinksByCubeId = {};
    this.cubeRelationships = {};
    this.foreignCubes = {};
    this.hasCombinations = true;

    this.mappedNonReferenceCubes = [];

    this.dataSource = "NO_DATA_SOURCE";
    this.selectedForeignDataSources = {};

    this.keepOrder = []; // order of items in keep
    this.keepState = {}; // state of items in keep

    this.filterOrder = []; // order of items in filter
    this.filterState = {}; // state of items in filter
    this.filterOperators = []; // array of "AND"s and "OR"s that connect items in filter
    this.filterOperatorStart = "";
    this.filterOperatorEnd = "";

    this.allowedValuesForVariableIds = {};
    this.allAllowedVariableIds = [];

    this.hasTemplateRepresentation = true;
    this.hasMembersInZAxis = false;
    this.hasOpenYAxis = false;

    this.selectAllForZAxis = false;

    this.cubeXYZ = [
      {
        byCombination: {},
        byOrdinates: {},
      },
    ];
    this.isTemplateRepresentationSelectionOn = false;
    this.templateRepresentationCells = {};
    this.templateRepresentationSelectedColumnsAndRows = {};
    this.templateRepresentationSelectedAll = {};

    this.allowedCombinationIds = new Set([]);

    this.seriesKeysFilter = "";

    this.selectedCubeLinks = [];
    this.selectedCubeRelationships = [];
    this.limit = 50; // LIMIT in SQL

    this.loadingCombinations = false;

    this.openFromCubeLinkPage = false;
    this.defaultFiltersFromMemberLinks = [];
  };

  @computed
  get isGroupByUsed() {
    return Object.values(this.keepState).find((item) => item.groupBy === true);
  }

  @computed
  get isShowMetadataUsed() {
    return Object.values(this.keepState).find(
      (item) => item.showMetadata === true
    );
  }
  @action SetHasCombinations = (hasCombination) => {
    this.hasCombinations = hasCombination;
  };

  @action SetHasTemplateRepresentation = (hasTemplateRepresentation) => {
    this.hasTemplateRepresentation = hasTemplateRepresentation;
  };

  @action SetWhereTRItemesContainItems = (value) => {
    this.whereTRItemsContainsItems = value;
  };

  @action
  getwhereTRItemsContainsItems() {
    return this.whereTRItemsContainsItems;
  }

  @action
  toggleShowQueryWizard = (state, loadCombinations) => {
    let newState = !this.showQueryWizard;
    if (state !== undefined) {
      newState = state;
    }

    // Call getCombinations when QueryWizard opens
    if (this.hasCombinations && newState && !this.showQueryWizard) {
      this.showQueryWizard = true;
      if (loadCombinations) {
        this.getCombinations();
      }
    } else {
      this.showQueryWizard = newState;
    }

    if (this.showQueryWizard) {
      const params = {
        type: "Statistics",
        filter: {
          type: "Statistics",
          statisticType: "LoadQueryWizard",
        },
      };
      this.http.sendQueryWizardAccessRequest(params);
    }
  };

  @action
  getCombinations = async () => {
    if (!this.hasCombinations) return;
    const cubeValidFrom = this.cubeDetails[0].validFrom;
    const cubeValidTo = this.cubeDetails[0].validTo;
    let validOn = formatDate(new Date()); // use today
    if (cubeValidFrom > validOn) {
      // if validity will start later, use validFrom
      validOn = cubeValidFrom;
    }
    if (cubeValidTo < validOn) {
      // if validity ended sooner, use validTo
      validOn = cubeValidTo;
    }

    const filterStateObjects = Object.values(this.filterState).filter(
      // Only keep those that are from the primary cube
      (stateObj) => !this.isForeignVariable(stateObj.itemIndex)
    );

    const variableIds = filterStateObjects.map(
      (stateObj) =>
        this.cubeStructureItems[stateObj.itemIndex].object.variableId
    );

    // do not filter wizardFilters by CSI.isImplemented
    // as we want the backend to filter out combinations also for logical/not implemented CSI
    const wizardFilters = filterStateObjects
      .filter(
        (stateObj) =>
          (stateObj.kind === "enumeratedSubdomain" ||
            stateObj.kind === "enumeratedVariableSet") &&
          stateObj.values.length > 0 &&
          !stateObj.focused
      )
      .map((stateObj) => {
        return {
          variableId:
            this.cubeStructureItems[stateObj.itemIndex].object.variableId,
          memberIdsFromSubdomain:
            stateObj.kind === "enumeratedSubdomain" ? stateObj.values : [],
          variableIdsFromVariableSet:
            stateObj.kind === "enumeratedVariableSet" ? stateObj.values : [],
        };
      });

    const csiByVariableIdByCubeStructureId = new Map();
    Object.values(this.cubeStructureItems).forEach((csi) => {
      const csiObject = csi.object;
      if (!csiByVariableIdByCubeStructureId.has(csiObject.cubeStructureId)) {
        csiByVariableIdByCubeStructureId.set(
          csiObject.cubeStructureId,
          new Map()
        );
      }
      const csiByVariableId = csiByVariableIdByCubeStructureId.get(
        csiObject.cubeStructureId
      );
      csiByVariableId.set(csiObject.variableId, csiObject);
    });

    if (this.controllerForGetCombinations && this.loadingCombinations) {
      this.controllerForGetCombinations.abort();
    }

    const controller = new window.AbortController();
    const signal = controller.signal;
    this.controllerForGetCombinations = controller;

    let saveAllowedCombinationIds = this.allowedCombinationIds;
    try {
      let params = {};
      if (this.cubeDetails[0]) {
        this.allowedCombinationIds = new Set([]);

        for (const cubeDetails of this.cubeDetails) {
          params = {
            cubeTid: cubeDetails.tid,
            validOn,
            variableIds,
            wizardFilters,
          };
          // changes applied here to support different cube structures from the CubeHierarchy/DataModel view
          if (!!params?.wizardFilters) {
            const csiByVariableId = csiByVariableIdByCubeStructureId.get(
              cubeDetails.cubeStructureId
            );
            params.wizardFilters.forEach((wizardFilter) => {
              if (csiByVariableId.has(wizardFilter.variableId)) {
                const csi = csiByVariableId.get(wizardFilter.variableId);
                wizardFilter.csiRole = csi.role;
              }
            });
          }
          
          params.includeCombinationIds = true;

          this.loadingCombinations = true;
          const json = await this.http.fetchQueryWizardCombinations(
            params,
            signal
          );
          if (
            json.error ||
            (json.errorMessage !== null && json.errorMessage !== undefined)
          ) {
            this.loadingCombinations = false;
            return;
          }
          runInAction(() => {
            function getUniqueListBy(arr, key) {
              return [
                ...new Map(arr.map((item) => [item[key], item])).values(),
              ];
            }
            this.allowedValuesForVariableIds = {
              ...json.allowedValuesForVariableIds,
              ...this.allowedValuesForVariableIds,
            };
            for (let key in this.allowedValuesForVariableIds) {
              if (json.allowedValuesForVariableIds.hasOwnProperty(key)) {
                this.allowedValuesForVariableIds[key] =
                  this.allowedValuesForVariableIds[key].concat(
                    json.allowedValuesForVariableIds[key]
                  );
                this.allowedValuesForVariableIds[key] = getUniqueListBy(
                  this.allowedValuesForVariableIds[key],
                  "tid"
                );
              }
            }

            this.allAllowedVariableIds = json.allAllowedVariableIds;
            this.removeDisallowedValues();

            if (json.allowedCombinationIds) {
              this.allowedCombinationIds = new Set([
                ...json.allowedCombinationIds,
                ...this.allowedCombinationIds,
              ]);
            }
          });
        }
      }
      this.loadingCombinations = false;
    } catch (error) {
      if (error.name === "AbortError") {
        this.allowedCombinationIds = saveAllowedCombinationIds;
        console.log("Combinations request aborted");
      } else {
        console.error("Error getting QueryWizard combinations.", error);
        this.loadingCombinations = false;
      }
    }
  };

  /**
   * - Returns array if cube has combinations. In such case, if combinations haven't loaded yet,
   *   empty array is returned.
   * - Returns `undefined` if cube has no combinations or particular variable is from a foreign cube.
   *
   * @param {string} itemID ID is a unique key in filterState object
   * @returns {Array | undefined}
   */
  getAllowedMembers = (itemID) => {
    const variableId =
      this.cubeStructureItems[this.filterState[itemID].itemIndex].object
        .variableId;
    if (this.isForeignVariable(this.filterState[itemID].itemIndex)) {
      return;
    }
    if (this.allowedValuesForVariableIds[variableId]) {
      return this.allowedValuesForVariableIds[variableId];
    }
    if (this.hasCombinations) {
      return [];
    }
    return;
  };

  @action
  removeDisallowedValues = () => {
    for (const stateObj of Object.values(this.filterState)) {
      const variableId =
        this.cubeStructureItems[stateObj.itemIndex].object.variableId;
      if (
        stateObj.values.length > 0 &&
        this.allowedValuesForVariableIds[variableId] &&
        !this.isForeignVariable(stateObj.itemIndex)
      ) {
        // intersection of all variable members and allowed members in combination
        stateObj.values = stateObj.values.filter((value) => {
          const found = this.allowedValuesForVariableIds[variableId].find(
            (allowedValue) =>
              allowedValue?.memberId === value ||
              allowedValue?.variableId === value
          );
          if (!found) {
            const item = this.cubeStructureItems[stateObj.itemIndex];
            const variable = item.items.find(
              (x) => x.object.type === "Variable"
            );
            toastService.notifyWithComponent(
              <CubeQueryWizardMemberRemovalWarning
                member={value}
                variableName={variable.object.name}
              />
            );
          }
          return found;
        });
      }
    }
  };

  /**
   * Get full list of members from backend for a field that's a subdomain.
   *
   * @param {number} itemIndex Index in this.cubeStructureItems
   * @param {number | string} id Actual id of cube structure item, used as key in filterState
   */
  @action
  loadMembersForSubdomain = async (itemIndex, id) => {
    const cubeStructureItem = this.cubeStructureItems[itemIndex];
    const subdomain = cubeStructureItem.items.find(
      (x) => x.object.type === "Subdomain"
    );
    if (
      subdomain &&
      subdomain.items &&
      subdomain.totalCountOfItems &&
      subdomain.totalCountOfItems > subdomain.items.length
    ) {
      const cubeValidFrom = this.cubeDetails[0].validFrom;
      const cubeValidTo = this.cubeDetails[0].validTo;
      let validOn = formatDate(new Date()); // use today
      if (cubeValidFrom > validOn) {
        // if validity will start later, use validFrom
        validOn = cubeValidFrom;
      }
      if (cubeValidTo < validOn) {
        // if validity ended sooner, use validTo
        validOn = cubeValidTo;
      }

      // Request params
      const params = {
        type: "Subdomain",
        tid: subdomain.object.tid,
        validOnForRelatedObjects: validOn,
        cubeTypes: [],
        resultsInTree: true,
        resultsInBuckets: true,
      };

      try {
        this.filterState[id].loading = true;
        const result = await this.http.fetchEntityDetails(params, true);
        this.filterState[id].loading = false;

        const rootItem =
          result.item.navigationLookupResult.navigationLookupRootItem;

        const items = rootItem.items
          .filter((item) => {
            // See: GenericListRepresentation.js .
            // We support this view only for the following enumerated types: VariableSet, Subdomain
            if (rootItem.object.type === "VariableSet")
              return (
                item.items &&
                item.items[0] &&
                item.items[0].object.type === "Variable"
              );
            if (rootItem.object.type === "Subdomain")
              return (
                item.items &&
                item.items[0] &&
                item.items[0].object.type === "Member"
              );
            return false;
          })
          .sort((a, b) =>
            a.items[0].object.code.localeCompare(b.items[0].object.code)
          );

        runInAction(() => {
          subdomain.items = items;
        });
      } catch (e) {
        toastService.func_notify(
          "Failed to download more enumerated items. Check your network connection.",
          "error"
        );
      }
    }
  };

  /**
   * Get full list of variables from backend for a field that's a variable set.
   *
   * @param {number} itemIndex Index in this.cubeStructureItems
   * @param {number | string} id Actual id of cube structure item, used as key in filterState
   */
  @action
  loadVariablesForVariableSet = async (itemIndex, id) => {
    const cubeStructureItem = this.cubeStructureItems[itemIndex];
    const variableSet = cubeStructureItem.items.find(
      (x) => x.object.type === "VariableSet"
    );
    if (
      variableSet &&
      variableSet.items &&
      variableSet.totalCountOfItems &&
      variableSet.totalCountOfItems > variableSet.items.length
    ) {
      const cubeValidFrom = this.cubeDetails[0].validFrom;
      const cubeValidTo = this.cubeDetails[0].validTo;
      let validOn = formatDate(new Date()); // use today
      if (cubeValidFrom > validOn) {
        // if validity will start later, use validFrom
        validOn = cubeValidFrom;
      }
      if (cubeValidTo < validOn) {
        // if validity ended sooner, use validTo
        validOn = cubeValidTo;
      }

      // Request params
      const params = {
        type: "VariableSet",
        tid: variableSet.object.tid,
        validOnForRelatedObjects: validOn,
        cubeTypes: [],
        resultsInTree: true,
        resultsInBuckets: true,
      };

      try {
        this.filterState[id].loading = true;
        const result = await this.http.fetchEntityDetails(params, true);
        this.filterState[id].loading = false;

        const rootItem =
          result.item.navigationLookupResult.navigationLookupRootItem;

        const items = rootItem.items
          .filter((item) => {
            // See: GenericListRepresentation.js .
            // We support this view only for the following enumerated types: VariableSet, variableSet
            if (rootItem.object.type === "VariableSet")
              return (
                item.items &&
                item.items[0] &&
                item.items[0].object.type === "Variable"
              );
            return false;
          })
          .sort((a, b) => {
            const aVar = a.items.find(
              (subItem) => subItem.object.type === "Variable"
            );
            const bVar = b.items.find(
              (subItem) => subItem.object.type === "Variable"
            );

            return aVar.object.code.localeCompare(bVar.object.code);
          });

        runInAction(() => {
          variableSet.items = items;
        });
      } catch (e) {
        toastService.func_notify(
          "Failed to download more enumerated items. Check your network connection.",
          "error"
        );
      }
    }
  };

  variablesSelectOptions = (sqlQueryPart = null) => {
    // Primary Cube
    let variablesOptions = Object.entries(this.cubeStructureItems).map(
      ([key, item]) => {
        let disabled = false;
        if (sqlQueryPart === CUBE_QUERY_WIZARD_SQL_QUERY_PART.SELECT_FIELDS) {
          disabled = !item.object.isImplemented;
        } else if (
          sqlQueryPart === CUBE_QUERY_WIZARD_SQL_QUERY_PART.FILTER_FIELDS
        ) {
          if (this.hasTemplateRepresentation) {
            disabled = false;
          } else {
            disabled = !item.object.isImplemented;
          }
        }
        const cubeId = key.slice(0, key.lastIndexOf("."));
        let variable = item.items.find((x) => x.object.type === "Variable");
        const isForeignCube = this.isForeignCube(cubeId);
        let cubeName = isForeignCube
          ? this.foreignCubes[cubeId]?.details?.name
          : null;
        if (isForeignCube && this.cubeDetails[0].name)
          cubeName = this.cubeDetails[0].name;
        return {
          cubeLabel: this.hasForeignCubes ? cubeName : undefined,
          label: `${variable.object.name || "[No name]"} (${
            item.object.cubeVariableCode
          }) [${item.object.role.toUpperCase()}]`,
          value: key, // `${cubeId}.{csiTid}`
          disabled,
          isImplemented: item.object.isImplemented,
          parentDimensionType: item?.object?.dimensionType?.toUpperCase(),
        };
      }
    );

    const uniqueVariableLabels = [
      ...new Map(variablesOptions.map((item) => [item["label"], item])).keys(),
    ];

    const uniqueVariableOptionsToUse = [];
    uniqueVariableLabels.forEach((variableLabel) => {
      const variables = variablesOptions.filter(
        (variable) => variable.label === variableLabel
      );
      if (variables.length > 1) {
        // if there are multiple same variables, we use this one that is not implemented (isImplemented = false). Otherwise we use the implemented one.
        const implementedVariable = variables.find(
          (variable) => !variable.isImplemented
        );
        if (!!implementedVariable) {
          uniqueVariableOptionsToUse.push(implementedVariable);
        } else {
          uniqueVariableOptionsToUse.push(variables[0]);
        }
      } else {
        uniqueVariableOptionsToUse.push(...variables);
      }
    });
    return uniqueVariableOptionsToUse;
  };

  // Keep

  @action
  changeKeepOrder = (itemIndices) => {
    let newItems = [];
    const newLength = itemIndices.length;
    const oldLength = this.keepOrder.length;
    let newItemKeepState = {};
    if (newLength > oldLength) {
      // new item(s) added
      newItems = itemIndices.slice(oldLength);
      for (const itemIndex of newItems) {
        newItemKeepState[itemIndex] = {
          groupBy: false,
          showMetadata: false,
          aggregationFunction: "",
          alias: "",
        };
      }
    }
    this.keepOrder = itemIndices;
    this.keepState = {
      ...this.keepState,
      ...newItemKeepState,
    };
  };

  @action
  changeGroupBy = (itemIndex) => {
    this.keepState = {
      ...this.keepState,
      [itemIndex]: {
        ...this.keepState[itemIndex],
        groupBy: !(
          this.keepState[itemIndex] && this.keepState[itemIndex].groupBy
        ),
      },
    };
  };

  @action
  changeShowMetadata = (itemIndex) => {
    this.keepState = {
      ...this.keepState,
      [itemIndex]: {
        ...this.keepState[itemIndex],
        showMetadata: !(
          this.keepState[itemIndex] && this.keepState[itemIndex].showMetadata
        ),
      },
    };
  };

  @action
  changeAlias = (alias, itemIndex) => {
    this.keepState = {
      ...this.keepState,
      [itemIndex]: {
        ...this.keepState[itemIndex],
        alias,
      },
    };
  };

  @action
  changeAggregationFunction = (aggrFn, itemIndex) => {
    this.keepState = {
      ...this.keepState,
      [itemIndex]: {
        ...this.keepState[itemIndex],
        aggregationFunction: aggrFn,
      },
    };
  };

  @action
  deleteKeep = (itemIndex) => {
    const orderIndex = this.keepOrder.findIndex((index) => index === itemIndex);
    delete this.keepState[itemIndex];
    this.keepOrder = [
      ...this.keepOrder.slice(0, orderIndex),
      ...this.keepOrder.slice(orderIndex + 1),
    ];
  };

  @action
  deleteAllKeepItems = () => {
    this.keepOrder = [];
    this.keepState = {};
  };

  // Filter

  @action
  changeFilterOrder = (itemIDs) => {
    this.filterOrder = itemIDs;
  };

  getItemKind = (itemKey) => {
    const cubeStructureItem = this.cubeStructureItems[itemKey];

    const combinationsIncludeThisVariable =
      this.hasCombinations &&
      this.allAllowedVariableIds.includes(cubeStructureItem.object.variableId);
    let kind = getFilterItemKind(
      cubeStructureItem,
      combinationsIncludeThisVariable
    );
    return kind;
  };

  isEnumerated = (itemKey) => {
    const kind = this.getItemKind(itemKey);
    return kind === "enumeratedSubdomain" || kind === "enumeratedVariableSet";
  };

  /**
   * @param {string[]} itemIndices e.g. ["2", "10", "0"]
   */
  @action
  addFilterItem = (itemIndices) => {
    const newItemKey = itemIndices[itemIndices.length - 1];
    const id = generateShortID();
    let kind = this.getItemKind(newItemKey);

    // default values for equalityOption variable
    let equalityOption;
    switch (kind) {
      case "date-range":
        equalityOption = "Range";
        break;
      case "number-range":
        equalityOption = "Range";
        break;
      case "string":
        equalityOption = "Contains";
        break;
      default:
        equalityOption = "Contains";
    }

    let values;
    switch (kind) {
      case "enumeratedSubdomain":
        values = [];
        break;
      case "enumeratedVariableSet":
        values = [];
        break;
      case "date-range":
        values = {
          singleDate: null,
          from: null,
          to: null,
        };
        break;
      case "string":
        values = "";
        break;
      case "number-range":
        values = {
          numbers: "",
          from: "",
          to: "",
        };
        break;
      default:
        values = {
          from: "",
          to: "",
        };
    }
    this.filterOperators = [...this.filterOperators, "AND"];

    this.filterOrder = [...this.filterOrder, id];
    this.filterState = {
      ...this.filterState,
      [id]: {
        kind,
        values,
        itemIndex: newItemKey,
        focused: false,
        loading: false,
        equalityOption: equalityOption,
      },
    };

    if (kind === "enumeratedSubdomain") {
      if (!this.isForeignVariable(newItemKey)) {
        this.getCombinations();
      }
      this.loadMembersForSubdomain(newItemKey, id);
    }

    if (kind === "enumeratedVariableSet") {
      this.loadVariablesForVariableSet(newItemKey, id);
    }

    return id;
  };

  @action
  updateFilterItemValues = (values, itemID, equalityOption) => {
    this.filterState = {
      ...this.filterState,
      [itemID]: {
        ...this.filterState[itemID],
        values,
        equalityOption,
      },
    };
  };

  @action
  deleteFilter = (itemID) => {
    const orderIndex = this.filterOrder.findIndex((id) => id === itemID);
    this.filterOperators = this.filterOperators.slice(
      0,
      this.filterOperators.length - 1
    ); // remove last item

    const kind = this.filterState[itemID].kind;
    const itemKey = this.filterState[itemID].itemIndex;

    delete this.filterState[itemID];
    this.filterOrder = [
      ...this.filterOrder.slice(0, orderIndex),
      ...this.filterOrder.slice(orderIndex + 1),
    ];

    if (kind === "enumeratedSubdomain" && !this.isForeignVariable(itemKey)) {
      this.getCombinations();
    }
  };

  isFilterItemDisabled = (itemID) => {
    const isForeignItem = this.isForeignVariable(
      this.filterState[itemID].itemIndex
    );

    return (
      !isForeignItem &&
      this.filterState[itemID].kind === "enumeratedSubdomain" &&
      ((this.loadingCombinations && !this.filterState[itemID].focused) ||
        (this.hasCombinations && this.getAllowedMembers(itemID).length === 0))
    );
  };

  @action
  deleteAllFilterItems = () => {
    this.filterOrder = [];
    this.filterState = {};
  };

  changeFilterOperator = (index, value) => {
    this.filterOperators = [
      ...this.filterOperators.slice(0, index),
      value,
      ...this.filterOperators.slice(index + 1),
    ];
  };

  focusFilterItem = (itemID) => {
    this.filterState[itemID].focused = true;

    if (!this.isForeignVariable(this.filterState[itemID].itemIndex)) {
      this.getCombinations();
    }
  };

  unfocusFilterItem = (itemID) => {
    this.filterState[itemID].focused = false;

    if (!this.isForeignVariable(this.filterState[itemID].itemIndex)) {
      this.getCombinations();
    }
  };

  /**
   * Variable Filter selection is disabled if there is any input in series keys.
   * See also: `filterBySeriesKeysDisabled`
   *
   * @todo This getter temporarily returns false all the time as per SDD-296 because of BSI
   * where we want series keys and variable filters selectable at the same time.
   */
  @computed
  get filterByVariableDisabled() {
    // return this.seriesKeysFilter !== "";

    // temporarily return false always - see SDD-296
    return false;
  }

  /**
   * Series Key input is disabled if there are any variables in filter and there are no foreign cubes.
   * See also: `filterByVariableDisabled`
   *
   * @todo This getter temporarily returns false all the time as per SDD-296 because of BSI
   * where we want series keys and variable filters selectable at the same time.
   */
  @computed
  get filterBySeriesKeysDisabled() {
    // For now, we do not disable series_key filter if there is a foreign cube
    // return this.filterOrder.length !== 0 && !this.hasForeignCubes;

    // temporarily return false always - see SDD-296
    return false;
  }

  // Template Representation

  /**
   * This function traverses the X and Y axes for given Z
   * and creates the cache data structure CubeXYZ
   * to keep reference between x, y, z and combination IDs.
   *
   * @param {*} refXAxis
   * @param {*} refYAxis
   * @param {*} zAxis
   * @param {*} combinations
   */
  @action createCubeXYZ = (
    refXAxis,
    refYAxis,
    zAxis,
    combinations,
    index,
    tableCode,
    selectedMember
  ) => {
    this.selectedMember = selectedMember;
    for (let y = 0; y < refYAxis.axisOrdinates.length; y++) {
      for (let x = 0; x < refXAxis.axisOrdinates.length; x++) {
        let targetCombinationId;
        let targetSystemDataCode;

        const isAbstract =
          refYAxis.axisOrdinates[y].isAbstractHeader ||
          refXAxis.axisOrdinates[x].isAbstractHeader;

        const commonXY = refYAxis.axisOrdinates[y].combinationIds.filter(
          (value) =>
            value && refXAxis.axisOrdinates[x].combinationIds.includes(value)
        );
        const dataCodes = refYAxis.axisOrdinates[y].systemDataCodes.filter(
          (value) =>
            value && refXAxis.axisOrdinates[x].systemDataCodes.includes(value)
        );
        const codeForX = refXAxis.axisOrdinates[x].code;
        const codeForY = refYAxis.axisOrdinates[y].code;
        if (
          zAxis &&
          selectedMember &&
          Array.isArray(selectedMember) &&
          index < selectedMember.length &&
          selectedMember[index] &&
          Array.isArray(selectedMember[index][0]) &&
          selectedMember[index][0].length > 0 &&
          selectedMember[index][0][0].combinationIds
        ) {
          const commonXYZ = commonXY.filter((combId) =>
            selectedMember[index][0][0]?.combinationIds.includes(combId)
          );
          if (commonXYZ.length > 0) {
            targetCombinationId = commonXYZ[0];
            targetSystemDataCode = dataCodes[0];
          }
        } else {
          if (commonXY.length > 0) {
            targetCombinationId = commonXY[0];
            targetSystemDataCode = dataCodes[0];
          }
        }
        if (targetCombinationId) {
          const selectedZ = this.getSelectedMemberIfDefined(undefined);
          const codeForZ = selectedZ && selectedZ && selectedZ.code;
          this.setCubeOrdinatesXYZ(
            targetCombinationId,
            codeForX,
            codeForY,
            codeForZ,
            isAbstract,
            index,
            targetSystemDataCode,
            tableCode
          );
        }
      }
    }
  };

  /**
   * This should always give a proper Z axis ordinate code.
   * However, if there are members in Z axis, there is no proper ordinate
   * and Z axis ordinate code is `undefined`.
   *
   * @param {*} zOrdinate - Contains string property code
   * @returns
   */
  getCurrentZOrdinateCode = () => {
    const zOrdinate = this.getSelectedMemberIfDefined(undefined);
    let z = undefined; // explicitly undefined
    if (!this.hasMembersInZAxis && zOrdinate && zOrdinate.code) {
      z = zOrdinate.code;
    }
    return z;
  };

  getSelectedMemberIfDefined = (fallbackValue) => {
    return (
      (this.selectedMember?.length > 0 &&
        this.selectedMember[0]?.length > 0 &&
        this.selectedMember[0][0]?.length > 0) ||
      fallbackValue
    );
  };

  /**
   * setCubeOrdinatesXYZ caches the cube structure in memory so that we can quickly
   * retrieve ordinates based on combination ID or vice versa.
   *
   * @param {string} combinationId
   * @param {string} x
   * @param {string} y
   * @param {string} [zCode] - optional
   */
  @action setCubeOrdinatesXYZ = (
    combinationId,
    x,
    y,
    zCode,
    isAbstract,
    index,
    systemDataCode,
    tableCode
  ) => {
    let z = zCode;
    if (this.hasMembersInZAxis) {
      z = undefined;
    }
    if (index >= this.cubeXYZ.length || !this.cubeXYZ[index]) {
      while (this.cubeXYZ.length <= index) {
        this.cubeXYZ.push(undefined);
      }

      this.cubeXYZ[index] = {
        byCombination: {},
        byOrdinates: {},
      };
    }

    if (!this.cubeXYZ[index].byCombination[combinationId]) {
      this.cubeXYZ[index].byCombination[combinationId] = [];
    }
    this.cubeXYZ[index].byCombination[combinationId].push({
      x,
      y,
      z,
      isAbstract,
    });
    if (!this.cubeXYZ[index].byOrdinates[z]) {
      this.cubeXYZ[index].byOrdinates[z] = {};
    }
    if (!this.cubeXYZ[index].byOrdinates[z][y]) {
      this.cubeXYZ[index].byOrdinates[z][y] = {};
    }
    if (!this.cubeXYZ[index].byOrdinates[z][y][x]) {
      this.cubeXYZ[index].byOrdinates[z][y][x] = {
        combinationId: combinationId,
        systemDataCode: systemDataCode,
        tableCode: tableCode,
      };
    }
  };

  @action
  setHasMembersInZAxis = (item, zAxis) => {
    if (zAxis && item && item.type === "AxisOrdinate") {
      this.hasMembersInZAxis = false;
    } else {
      this.hasMembersInZAxis = true;
    }
  };

  /**
   * Main toggle to turn on/off selection of data points
   * in Template Representation.
   *
   * @param {boolean} state If undefined, just toggle to the opposite state.
   */
  @action
  toggleTemplateRepresentationSelection = (state) => {
    runInAction(() => {
      if (state !== undefined) {
        this.isTemplateRepresentationSelectionOn = state;
      } else {
        this.isTemplateRepresentationSelectionOn =
          !this.isTemplateRepresentationSelectionOn;
      }
      this.getCombinations();
    });
  };

  isCombinationIdAllowed = (combinationId) => {
    return this.allowedCombinationIds.has(combinationId);
  };

  getTemplateRepresentationCellState = (combinationId, x, y, index) => {
    const disabled = !this.isCombinationIdAllowed(combinationId);
    const z = this.getCurrentZOrdinateCode();
    // This returns the actual state of selected combination
    const cells = this.templateRepresentationCells;
    return (
      !disabled &&
      (cells[index] &&
        cells[index][z] &&
        cells[index][z][y] &&
        cells[index][z][y][x]) !== undefined
    );
  };

  canSelectMultipleCells = () => {
    return (
      this.isTemplateRepresentationSelectionOn && !this.loadingCombinations
    );
  };

  @action
  toggleTemplateRepresentationCellState = (
    combinationId,
    x,
    y,
    zOrdinate,
    value,
    index = 0
  ) => {
    const cells = this.templateRepresentationCells;
    zOrdinate = this.getSelectedMemberIfDefined(false);
    let z = zOrdinate && zOrdinate.code;
    if (this.hasMembersInZAxis) {
      z = undefined;
    }

    if (!this.isCombinationIdAllowed(combinationId)) {
      return;
    }
    if (
      cells[index] &&
      cells[index][z] &&
      cells[index][z][y] &&
      cells[index][z][y][x] &&
      value !== true
    ) {
      delete cells[index][z][y][x];
      if (Object.keys(cells[index][z][y]).length === 0) {
        delete cells[index][z][y];
        if (Object.keys(cells[index][z]).length === 0) {
          delete cells[index][z];
        }
      }
      return;
    }
    if (
      cells[index] &&
      cells[index][z] &&
      cells[index][z][y] &&
      cells[index][z][y][x] &&
      value === true
    ) {
      // combination ID is already selected, so keep it that way
      return;
    }
    if (
      (!cells[index] ||
        !cells[index][z] ||
        !cells[index][z][y] ||
        !cells[index][z][y][x]) &&
      value === false
    ) {
      // combination ID is already unselected, so keep it that way
      return;
    }

    // We want to check ✅ the cell if
    //   (!cells[z] || !cells[z][y] || !cells[z][y][x]) && (value === true || value === undefined) ...
    if (!cells[index]) {
      cells[index] = {};
    }
    if (!cells[index][z]) {
      cells[index][z] = {};
    }
    if (!cells[index][z][y]) {
      cells[index][z][y] = {};
    }
    cells[index][z][y][x] = true;
  };

  @action
  toggleOpenFromCubeLinkPage = (value) => {
    this.openFromCubeLinkPage = value;
  };

  @action
  extractMembersFromMemberLinks = async (a) => {
    this.defaultFiltersFromMemberLinks.push(a);
  };

  @action
  resetDefaultFiltersFromMemberLinks = () => {
    this.defaultFiltersFromMemberLinks = [];
  };

  @action
  populateDefaultFiltersFromMemberLinks = async () => {
    const variableOptions = this.variablesSelectOptions();
    const variableMemberPair = this.defaultFiltersFromMemberLinks;

    const filteredVariables = variableOptions.filter((x) => {
      const object = variableMemberPair.filter((y) =>
        x.label.includes(`(${y.code})`)
      );
      if (object && object.length > 0) {
        const result = x;
        result.members = object[0];
        return result;
      } else {
        return [];
      }
    });

    filteredVariables.map(async (x) => {
      runInAction(() => {
        const itemID = this.addFilterItem([x.value]);
        const values = x.members.memberIds;
        this.updateFilterItemValues(values, itemID);
      });
    });
  };

  @action
  populateDefaultFiltersFromCubes = async () => {
    if (Object.values(this.filterState).length !== 0) return;
    const variableOptions = this.variablesSelectOptions();
    const variableMemberPair = this.defaultFiltersFromMemberLinks;
    const filteredVariables = variableOptions.filter((x) => {
      if (x.parentDimensionType === "T" || x.parentDimensionType === "E") {
        return true;
      }

      const object = variableMemberPair.filter((y) =>
        x.label.includes(`(${y.code})`)
      );

      if (object && object.length > 0) {
        x.members = object[0];
        return true;
      }

      return false;
    });

    for (const x of filteredVariables) {
      runInAction(() => {
        this.addFilterItem([x.value]);
      });
    }
  };

  /**
   * Common function for selecting both columns and rows.
   *
   * @param {"column" | "row"} type - Must be either "column" or "row".
   * @param {*} ordinateToSelect
   * @param {*} zOrdinate
   * @param {boolean} [stateValue] - Optional, the final value of checkboxes. Default is the opposite of current.
   */
  toggleTemplateRepresentationSelectColumnOrRow = (
    type,
    ordinateToSelect,
    zOrdinate,
    stateValue,
    index = 0
  ) => {
    zOrdinate = this.getSelectedMemberIfDefined(undefined);
    const zCode = this.getCurrentZOrdinateCode();
    const dimension = type === "column" ? "x" : "y";
    const key = `${type}_${ordinateToSelect.code}`;
    let newState = stateValue;

    runInAction(() => {
      if (!this.templateRepresentationSelectedColumnsAndRows[index]) {
        this.templateRepresentationSelectedColumnsAndRows[index] = {};
      }

      if (
        !this.templateRepresentationSelectedColumnsAndRows[index][zCode] &&
        newState !== false
      ) {
        this.templateRepresentationSelectedColumnsAndRows[index][zCode] = {};
      }

      if (
        this.templateRepresentationSelectedColumnsAndRows[index][zCode] &&
        this.templateRepresentationSelectedColumnsAndRows[index][zCode][key] &&
        newState !== true
      ) {
        delete this.templateRepresentationSelectedColumnsAndRows[index][zCode][
          key
        ];
        newState = false;
      } else if (
        this.templateRepresentationSelectedColumnsAndRows[index][zCode] &&
        !this.templateRepresentationSelectedColumnsAndRows[index][zCode][key] &&
        newState !== false
      ) {
        this.templateRepresentationSelectedColumnsAndRows[index][zCode][
          key
        ] = true;
        newState = true;
      }
      for (const id of ordinateToSelect.combinationIds) {
        if (this.cubeXYZ[index].byCombination[id]) {
          const cells = this.cubeXYZ[index].byCombination[id].filter(
            (ord) => ord[dimension] === ordinateToSelect.code
          );
          for (const cell of cells) {
            const { x, y } = cell;
            if (this.hasMembersInZAxis) {
              this.toggleTemplateRepresentationCellState(
                id,
                x,
                y,
                zOrdinate,
                newState,
                index
              );
            } else if (zOrdinate?.combinationIds?.includes(id)) {
              this.toggleTemplateRepresentationCellState(
                id,
                x,
                y,
                zOrdinate,
                newState,
                index
              );
            }
          }
        }
      }
    });
  };

  toggleTemplateRepresentationSelectColumn = (
    ordinateToSelect,
    zOrdinate,
    stateValue,
    index = 0
  ) => {
    this.toggleTemplateRepresentationSelectColumnOrRow(
      "column",
      ordinateToSelect,
      zOrdinate,
      stateValue,
      index
    );
  };

  toggleTemplateRepresentationSelectRow = (
    ordinateToSelect,
    zOrdinate,
    stateValue,
    index = 0
  ) => {
    this.toggleTemplateRepresentationSelectColumnOrRow(
      "row",
      ordinateToSelect,
      zOrdinate,
      stateValue,
      index
    );
  };

  getTemplateRepresentationRowSelectionState = (code, index) => {
    const zCode = this.getCurrentZOrdinateCode();
    const key = `row_${code}`;
    return (
      this.templateRepresentationSelectedColumnsAndRows[index] &&
      this.templateRepresentationSelectedColumnsAndRows[index][zCode] &&
      this.templateRepresentationSelectedColumnsAndRows[index][zCode][key]
    );
  };

  getTemplateRepresentationColumnSelectionState = (code, index) => {
    const zCode = this.getCurrentZOrdinateCode();
    const key = `column_${code}`;
    return (
      this.templateRepresentationSelectedColumnsAndRows[index] &&
      this.templateRepresentationSelectedColumnsAndRows[index][zCode] &&
      this.templateRepresentationSelectedColumnsAndRows[index][zCode][key]
    );
  };

  toggleSelectAllCells = (xAxes, yAxes, zOrdinate, stateValue, index) => {
    const xAxis = xAxes && xAxes.length > 0 ? xAxes[0] : undefined;
    const yAxis = yAxes && yAxes.length > 0 ? yAxes[0] : undefined;

    const zCode = this.getCurrentZOrdinateCode();

    let value =
      this.templateRepresentationSelectedAll[zCode] === undefined ||
      !this.templateRepresentationSelectedAll
        ? true
        : false;

    if (stateValue !== undefined) {
      value = stateValue;
    }

    runInAction(() => {
      if (!value) {
        delete this.templateRepresentationSelectedAll[zCode];
      } else {
        this.templateRepresentationSelectedAll[zCode] = true;
      }
      if (xAxis && xAxis.axisOrdinates && Array.isArray(xAxis.axisOrdinates)) {
        for (const ord of xAxis.axisOrdinates) {
          this.toggleTemplateRepresentationSelectColumn(
            ord,
            zOrdinate,
            value,
            index
          );
        }
      }
      if (yAxis && yAxis.axisOrdinates && Array.isArray(yAxis.axisOrdinates)) {
        for (const ord of yAxis.axisOrdinates) {
          this.toggleTemplateRepresentationSelectRow(
            ord,
            zOrdinate,
            value,
            index
          );
        }
      }
    });
  };

  @computed
  get allCellsStateSelected() {
    const zOrdinate = this.detailViewStore.getSelectedTemplateMembers[0];
    const zCode =
      (!this.hasMembersInZAxis && zOrdinate && zOrdinate.code) || undefined;
    return this.templateRepresentationSelectedAll[zCode];
  }

  get zAxisMemberCodes() {
    return this.detailViewStore.getSelectedTemplateMembers.map((m) => m.code);
  }

  @action
  setHasOpenYAxis = (yAxes, refYAxis) => {
    let hasOpenYAxis = false;
    for (let y = 0; y < refYAxis.axisOrdinates.length; y++) {
      for (const axis of yAxes) {
        if (axis.axisOrdinates[y]) {
          if (this.isOrdinateLinkedToVariable(axis.axisOrdinates[y])) {
            hasOpenYAxis = true;
          }
        }
      }
    }
    this.hasOpenYAxis = hasOpenYAxis;
  };

  isOrdinateLinkedToVariable = (ordinate) => {
    if (!ordinate.variable) {
      return;
    }
    const variable = ordinate.variable;
    const domain = variable?.items?.find((x) => x.object.type === "Domain");
    const csi = Object.values(this.cubeStructureItems).find((item) => {
      const csiVariable = item.items.find((x) => x.object.type === "Variable");
      return csiVariable.object.tid === variable.object.tid;
    });

    const isOpenYAxis =
      csi &&
      !domain.object.isEnumerated &&
      domain.object.dataType.toLowerCase() === "string";

    return isOpenYAxis;
  };

  @action
  setSelectAllForZAxis = (event) => {
    const value = event.target.checked;
    this.selectAllForZAxis = value;
  };

  @computed
  get valueByVariableTid() {
    const map = {};
    for (const filterItem of Object.values(this.filterState)) {
      const csi = this.cubeStructureItems[filterItem.itemIndex];
      const csiVariable = csi.items.find((x) => x.object.type === "Variable");
      const tid = csiVariable.object.tid;
      const value = filterItem.values;
      map[tid] = value;
    }
    return map;
  }

  setValueForOrdinateLinkedToVariable = (ordinate, value) => {
    if (!ordinate.variable) {
      return;
    }

    const variable = ordinate.variable;
    const keyInCubeStructureItems = Object.keys(this.cubeStructureItems).find(
      (itemKey) => {
        const item = this.cubeStructureItems[itemKey];
        const csiVariable = item.items.find(
          (x) => x.object.type === "Variable"
        );
        return csiVariable.object.tid === variable.object.tid;
      }
    );

    // Looking into filterState, if this variable already has filter
    const foundEntry = Object.entries(this.filterState).find(
      ([_key, item]) => item.itemIndex === keyInCubeStructureItems
    );

    if (foundEntry && foundEntry[0]) {
      // If variable has filter, update value
      this.updateFilterItemValues(value, foundEntry[0]); // foundEntry[0] is the key
    } else {
      // if not, create the filter first, then update its value
      runInAction(() => {
        const itemID = this.addFilterItem([keyInCubeStructureItems]);
        this.updateFilterItemValues(value, itemID);
      });
    }
  };

  /**
   * Returns variable code. E.g. `memberCodes[variableId][memberId]`.
   * Uses `this.allowedValuesForVariableIds`.
   */
  @computed
  get membersMap() {
    const result = {};
    if (this.hasCombinations) {
      if (this.allowedValuesForVariableIds) {
        for (const [variableId, valueArray] of Object.entries(
          this.allowedValuesForVariableIds
        )) {
          const cubeId = this.cubeDetails[0].cubeId;
          for (const value of valueArray) {
            if (!value?.memberId) {
              // means that this is not a member. Most likely a variable
              continue;
            }
            if (!result[cubeId]) {
              result[cubeId] = {};
            }
            if (!result[cubeId][variableId]) {
              result[cubeId][variableId] = {};
            }
            result[cubeId][variableId][value.memberId] = value;
          }
        }
      }
    }

    for (const [itemKey, item] of Object.entries(this.cubeStructureItems)) {
      const cubeId = this.getCubeDetailsByItemKey(itemKey).cubeId;
      const subdomain =
        item && item.items
          ? item.items.find((x) => x.object.type === "Subdomain")
          : null;
      if (
        (!this.hasCombinations || this.isForeignVariable(itemKey)) && // primary cubes with combinations have members stored in allowedValuesForVariableIds
        item.object &&
        item.object.variableId &&
        subdomain &&
        subdomain.items !== undefined &&
        subdomain.items !== null &&
        subdomain.items.length
      ) {
        const variableId = item.object.variableId;
        // Enumerated subdomain
        const enumeratedMembers = subdomain.items
          .filter((enumItem) => enumItem.items)
          .map((enumItem) => enumItem.items)
          .map((x) => x[0].object);
        for (const member of enumeratedMembers) {
          if (!result[cubeId]) {
            result[cubeId] = {};
          }
          if (!result[cubeId][variableId]) {
            result[cubeId][variableId] = {};
          }
          result[cubeId][variableId][member.memberId] = member;
        }
      }
    }
    return result;
  }

  @computed
  get variableSetMap() {
    const result = {};
    for (const item of Object.values(this.cubeStructureItems)) {
      const variableSet =
        item && item.items
          ? item.items.find((x) => x.object.type === "VariableSet")
          : null;
      if (
        item.object &&
        item.object.variableId &&
        variableSet &&
        variableSet.items !== undefined &&
        variableSet.items !== null &&
        variableSet.items.length
      ) {
        const variableId = item.object.variableId;
        // Enumerated variableSet
        const enumeratedVariableSet = variableSet.items
          .filter((enumItem) => {
            if (enumItem.items && enumItem.items.length) {
              const variable = enumItem.items.find(
                (subItem) => subItem.object.type === "Variable"
              );
              return variable !== undefined && variable !== null;
            }
            return false;
          })
          .map((enumItem) => enumItem.items)
          .map((enumItem) => {
            const variable = enumItem.find(
              (subItem) => subItem.object.type === "Variable"
            );
            return variable.object;
          });
        for (const variable of enumeratedVariableSet) {
          if (!result[variableId]) {
            result[variableId] = {};
          }
          result[variableId][variable.variableId] = variable;
        }
      }
    }
    return result;
  }

  @computed
  get selectedFilterItemIndexes() {
    return Object.values(this.filterState).map(({ itemIndex }) => itemIndex);
  }

  /**
   * If a cube has Template Representation and if a user filters by some variable / field,
   * then also selecting a data point from Template representation is mandatory.
   */
  @computed
  get shouldWarnAboutDataPointsMandatory() {
    const hasEnumField = Object.values(this.filterState).some(
      ({ kind }) => kind === "enumeratedSubdomain"
    );

    const hasLogicalCSI = Object.values(this.filterState).some(
      (fs) => this.cubeStructureItems[fs.itemIndex].object.isImplemented
    );

    const noDataPointsSelected =
      Object.values(this.templateRepresentationCells).length === 0;

    return (
      hasEnumField &&
      this.hasTemplateRepresentation &&
      noDataPointsSelected &&
      !hasLogicalCSI
    );
  }

  /**
   * Cube Links and Cube Relationships
   */

  /**
   *
   * @param {"CubeLink"|"CubeRelationship"} type Either CubeLink or CubeRelationship
   */
  @action
  loadCubeLinksOrRelationships = async (type = "CubeLink", cubeId) => {
    const params = {
      tableName: type,
      pageSize: 100,
      queryOffset: 0,
      filter: {
        primaryCubeId: {
          contains: cubeId || this.cubeDetails[0].cubeId,
        },
        bid: { in: [1] },
        sort: [],
        type: type,
      },
    };

    try {
      // when primaryCubeId === cubeId
      const cubeLinksOrRelationshipsJson =
        await this.http.fetchContentManagementTableData(params);

      if (cubeLinksOrRelationshipsJson.error) {
        throw new Error(cubeLinksOrRelationshipsJson.error.message);
      }
      if (type === "CubeRelationship") {
        // for CubeRelationship we want also the other direction: foreignCubeId === cubeId
        const paramsByForeign = {
          ...params,
          filter: {
            ...params.filter,
            primaryCubeId: undefined,
            foreignCubeId: params.filter.primaryCubeId,
          },
        };
        const cubeRelationshipsByForeignJson =
          await this.http.fetchContentManagementTableData(paramsByForeign);

        // we flip foreign and primary for these as the other logic depends on the naming
        cubeRelationshipsByForeignJson.results =
          cubeRelationshipsByForeignJson.results.map((item) => {
            return {
              ...item,
              foreignCubeId: item["primaryCubeId"],
              primaryCubeId: item["foreignCubeId"],
              foreignCubeMandatoriness: item["primaryCubeMandatoriness"],
              primaryCubeMandatoriness: item["foreignCubeMandatoriness"],
              foreignCubeVariableCode: item["primaryCubeVariableCode"],
              primaryCubeVariableCode: item["foreignCubeVariableCode"],
            };
          });

        // Merging results for cubeId being primary with the ones cubeId being foreign
        cubeLinksOrRelationshipsJson.results = [
          ...cubeLinksOrRelationshipsJson.results,
          ...cubeRelationshipsByForeignJson.results,
        ];

        /**
         * CubeRelationships is a list where one Cube can appear multiple times.
         * Cube + Variable is unique. We need to group by cubes.
         */
        const filteredAndGroupedByForeignCubeId =
          cubeLinksOrRelationshipsJson.results.reduce((result, item) => {
            if (item["foreignCubeId"] !== this.cubeDetails[0].cubeId) {
              if (!result[item["foreignCubeId"]]) {
                result[item["foreignCubeId"]] = [];
              }
              result[item["foreignCubeId"]].push(item);
            }
            return result;
          }, {});
        cubeLinksOrRelationshipsJson.results = Object.values(
          filteredAndGroupedByForeignCubeId
        ).map((groupedArray) => {
          const item = {
            ...groupedArray[0],
            onConditions: groupedArray,
          };
          return item;
        });

        runInAction(() => {
          for (const result of cubeLinksOrRelationshipsJson.results) {
            if (!this.cubeRelationships[result.foreignCubeId]) {
              this.cubeRelationships[result.foreignCubeId] = result;
            } else if (
              ON_CONDITIONS_FROM_DIFFERENT_RELATIONSHIPS_IN_SAME_JOIN
            ) {
              this.cubeRelationships[result.foreignCubeId] = {
                ...this.cubeRelationships[result.foreignCubeId], // we always keep the original cube relationship, i.e. the original primaryCubeId stays there
                onConditions: [
                  ...this.cubeRelationships[result.foreignCubeId].onConditions,
                  ...result.onConditions,
                ],
              };
            }
          }
        });
      }

      if (type === "CubeLink") {
        // Loading CubeStructureItemLinks
        const cubeStructureItemLinksJson = await Promise.all(
          cubeLinksOrRelationshipsJson.results.map(({ cubeLinkId }) => {
            const params = {
              tableName: "CubeStructureItemLink",
              pageSize: 100,
              queryOffset: 0,
              filter: {
                cubeLinkId: { contains: cubeLinkId },
                bid: { in: [1] },
                sort: [],
                type: "CubeStructureItemLink",
              },
            };
            return this.http.fetchContentManagementTableData(params);
          })
        );

        const cubeStructureItemLinksMap = {};
        for (const { results } of cubeStructureItemLinksJson) {
          if (results.length > 0) {
            cubeStructureItemLinksMap[results[0].cubeLinkId] = results;
          }
        }

        runInAction(() => {
          for (const result of cubeLinksOrRelationshipsJson.results) {
            const { foreignCubeId, cubeLinkId } = result;
            this.cubeStructureItemLinksByCubeId[foreignCubeId] =
              cubeStructureItemLinksMap[cubeLinkId];
          }
        });

        // Loading MemberLinks
        for (const cubeStructureItemLinks of Object.values(
          this.cubeStructureItemLinksByCubeId
        )) {
          const memberLinksJson = await Promise.all(
            cubeStructureItemLinks.map(({ cubeStructureItemLinkId }) => {
              const params = {
                tableName: "MemberLink",
                pageSize: 100,
                queryOffset: 0,
                filter: {
                  cubeStructureItemLinkId: {
                    contains: cubeStructureItemLinkId,
                  },
                  bid: { in: [1] },
                  sort: [],
                  type: "MemberLink",
                },
              };
              return this.http.fetchContentManagementTableData(params);
            })
          );

          const memberLinksMap = {};
          for (const { results } of memberLinksJson) {
            if (results.length > 0) {
              memberLinksMap[results[0].cubeStructureItemLinkId] = results;
            }
          }

          runInAction(() => {
            for (const cubeStructureItemLink of cubeStructureItemLinks) {
              if (
                memberLinksMap[cubeStructureItemLink.cubeStructureItemLinkId]
              ) {
                cubeStructureItemLink.memberLinks =
                  memberLinksMap[cubeStructureItemLink.cubeStructureItemLinkId];
              } else {
                cubeStructureItemLink.memberLinks = [];
              }
            }
          });
        }
      }

      // Loading Cubes
      const foreignCubesJson = await Promise.all(
        cubeLinksOrRelationshipsJson.results
          .filter(({ foreignCubeId }) => !this.foreignCubes[foreignCubeId]) // only take those that don't exist yet
          .map(({ foreignCubeId }) => {
            const params = {
              tableName: "Cube",
              pageSize: 100,
              queryOffset: 0,
              filter: {
                cubeId: { contains: foreignCubeId },
                bid: { in: [1] },
                sort: [],
                type: "Cube",
              },
            };
            return this.http.fetchContentManagementTableData(params);
          })
      );

      const foreignCubesResults = foreignCubesJson.map(
        ({ results }) => results[0]
      );

      runInAction(() => {
        for (const result of foreignCubesResults) {
          this.foreignCubes[result.cubeId] = {
            details: result,
          };
        }
      });
    } catch (error) {
      console.error(`Error loading ${type}s`, error);
    }
  };

  @action
  loadCubeDetails = async (cubeId) => {
    const cube = this.foreignCubes[cubeId];
    if (!cube) return;

    const details = this.foreignCubes[cubeId].details;
    const { tid, validFrom, validTo } = details;

    try {
      const cubeValidFrom = validFrom;
      const cubeValidTo = validTo;
      let validOn = formatDate(new Date()); // use today
      if (cubeValidFrom > validOn) {
        // if validity will start later, use validFrom
        validOn = cubeValidTo;
      }
      if (cubeValidTo < validOn) {
        // if validity ended sooner, use validTo
        validOn = cubeValidTo;
      }

      const params = {
        type: "Cube",
        tid: tid,
        validOnForRelatedObjects: validOn,
        cubeTypes: [],
        resultsInTree: true,
        resultsInBuckets: true,
      };
      const cubeJson = await this.http.fetchEntityDetails(params, true);

      this.foreignCubes[cubeId].loading = true;

      runInAction(() => {
        const root =
          cubeJson.item.navigationLookupResult.navigationLookupRootItem;
        const dataSetDistributions =
          cubeJson.item.otherInformation.dataSetDistributions;
        this.foreignCubes[cubeId].root = root;
        this.foreignCubes[cubeId].dataSetDistributions = dataSetDistributions;
      });

      const foreignCubesDataSources = this.foreignCubesDataSources;
      if (foreignCubesDataSources[cubeId].length === 1) {
        runInAction(() => {
          this.selectedForeignDataSources[cubeId] =
            foreignCubesDataSources[cubeId][0].table;
        });
      }
    } catch (error) {
      console.error(`Error loading details of cube ${cubeId}`, error);
    } finally {
      this.foreignCubes[cubeId].loading = false;
    }
  };

  @computed
  get cubeLinksOptions() {
    return Object.values(this.cubeLinks).map((cubeLink) => {
      const cubeDetails = this.foreignCubes[cubeLink.foreignCubeId].details;
      return {
        label: `${cubeDetails.name} (${cubeDetails.code})`,
        value: cubeLink.foreignCubeId,
      };
    });
  }

  @computed
  get cubeRelationshipsOptions() {
    return Object.values(this.cubeRelationships)
      .filter(
        (cubeRelationship) => this.foreignCubes[cubeRelationship.foreignCubeId]
      )
      .map((cubeRelationship) => {
        const cubeDetails =
          this.foreignCubes[cubeRelationship.foreignCubeId].details;
        return {
          label: `${cubeDetails.name} (${cubeDetails.code})`,
          value: cubeRelationship.foreignCubeId,
        };
      });
  }

  @computed
  get foreignCubesArray() {
    const cubeLinksCubes = this.selectedCubeLinks.map((cubeLinkTid) => {
      const foreignCubeId = this.cubeLinks[cubeLinkTid].foreignCubeId;
      const cube = this.foreignCubes[foreignCubeId];
      return cube;
    });
    const cubeRelationshipsCubes = this.selectedCubeRelationships.map(
      (foreignCubeId) => {
        const cube = this.foreignCubes[foreignCubeId];
        return cube;
      }
    );
    return [...cubeLinksCubes, ...cubeRelationshipsCubes];
  }

  @computed
  get foreignCubesDataSources() {
    const foreignCubesDataSources = {};
    for (const cube of this.foreignCubesArray) {
      if (cube.root && cube.dataSetDistributions) {
        foreignCubesDataSources[cube.details.cubeId] =
          getTablesFromDataSetDistributions(cube.dataSetDistributions).concat(
            getTablesFromDatasetUrl(cube.root.object)
          );
      } else {
        foreignCubesDataSources[cube.details.cubeId] = [];
      }
    }
    return foreignCubesDataSources;
  }

  @computed
  get cubeStructureItems() {
    const cubeStructureItems = {};
    if (
      !this.cubeRoot ||
      Object.keys(this.cubeRoot).length < 1 ||
      !this.cubeDetails ||
      Object.keys(this.cubeDetails).length < 1
    ) {
      return [];
    }

    // Primary cube
    let cubeStructure = [];
    if (this.cubeRoot.length > 0)
      for (let cubeRoot of this.cubeRoot) {
        cubeStructure.push(findCubeStructure(cubeRoot));
      }
    else cubeStructure = findCubeStructure(this.cubeRoot);

    // cubeStructure = findCubeStructure(this.cubeRoot[0]);

    if (cubeStructure.length > 0) {
      for (let cubeStructureItemss of cubeStructure) {
        for (const item of cubeStructureItemss.items) {
          cubeStructureItems[
            `${this.cubeDetails[0].cubeId}.${item.object.tid}`
          ] = item;
        }
      }
    } else
      for (const item of cubeStructure.items) {
        cubeStructureItems[`${this.cubeDetails[0].cubeId}.${item.object.tid}`] =
          item;
      }

    // Foreign cubes
    for (const [cubeId, cube] of Object.entries(this.foreignCubes)) {
      if (!cube.root) continue;
      const cubeStructure = findCubeStructure(cube.root);
      for (const item of cubeStructure.items) {
        cubeStructureItems[`${cubeId}.${item.object.tid}`] = item;
      }
    }
    return cubeStructureItems;
  }

  @computed
  get hasForeignCubes() {
    return (
      this.selectedCubeLinks.length + this.selectedCubeRelationships.length > 0
    );
  }

  isForeignCube = (cubeId) => {
    return cubeId !== this.cubeDetails[0].cubeId;
  };

  isForeignVariable = (itemKey) => {
    return (
      this.getCubeDetailsByItemKey(itemKey).cubeId !==
      this.cubeDetails[0].cubeId
    );
  };

  getCubeDetailsByItemKey = (itemKey) => {
    const cubeId = itemKey.slice(0, itemKey.lastIndexOf("."));
    if (this.isForeignCube(cubeId)) {
      if (this.foreignCubes[cubeId]) {
        return this.foreignCubes[cubeId]?.details;
      }
    } else {
      return this.cubeDetails[0];
    }
  };

  // Cube Links:

  @action
  addSelectedCubeLinksItem = (items) => {
    this.selectedCubeLinks = items;

    const newCubeId = items[items.length - 1];
    this.loadCubeDetails(newCubeId);
  };

  @action
  deleteSelectedCubeLinksItem = (itemId) => {
    const orderIndex = this.selectedCubeLinks.findIndex(
      (index) => index === itemId
    );
    this.selectedCubeLinks = [
      ...this.selectedCubeLinks.slice(0, orderIndex),
      ...this.selectedCubeLinks.slice(orderIndex + 1),
    ];
  };

  @action
  deleteAllSelectedCubeLinksItems = () => {
    this.selectedCubeLinks = [];
  };

  // Cube Relationships:

  @action
  addSelectedCubeRelationshipsItem = (items) => {
    this.selectedCubeRelationships = items;

    const newCubeId = items[items.length - 1];
    this.loadCubeDetails(newCubeId);
    this.loadCubeLinksOrRelationships("CubeRelationship", newCubeId);
  };

  @action
  deleteSelectedCubeRelationshipsItem = (cubeId) => {
    const orderIndex = this.selectedCubeRelationships.findIndex(
      (index) => index === cubeId
    );
    if (orderIndex === -1) return;

    this.selectedCubeRelationships = [
      ...this.selectedCubeRelationships.slice(0, orderIndex),
      ...this.selectedCubeRelationships.slice(orderIndex + 1),
    ];

    delete this.selectedForeignDataSources[cubeId];

    // Deleting from "Fields to be shown"
    for (const itemKey of this.keepOrder) {
      if (itemKey.startsWith(`${cubeId}.`)) {
        this.deleteKeep(itemKey);
      }
    }

    // Deleting from "Fields to filter"
    for (const itemKey of this.filterOrder) {
      if (this.filterState[itemKey].itemIndex.startsWith(`${cubeId}.`)) {
        this.deleteFilter(itemKey);
      }
    }

    // Deleting cube itself
    delete this.foreignCubes[cubeId].root;
    delete this.foreignCubes[cubeId].dataSetDistributions;

    // Delete all cube relationships where the primaryCubeId is the currently deleted cubeId
    runInAction(() => {
      for (const [foreignCubeId, cubeRelationship] of Object.entries(
        this.cubeRelationships
      )) {
        if (cubeId === cubeRelationship.primaryCubeId) {
          // First recursively delete all dependent cubes
          const foreignOrderIndex = this.selectedCubeRelationships.findIndex(
            (index) => index === foreignCubeId
          );
          if (foreignOrderIndex >= 0) {
            this.deleteSelectedCubeRelationshipsItem(foreignCubeId);
          }

          // Then delete cube relationship itself
          delete this.cubeRelationships[foreignCubeId];
        }
      }
    });
  };

  @action
  deleteAllSelectedCubeRelationshipsItems = () => {
    runInAction(() => {
      for (const cubeId of this.selectedCubeRelationships) {
        this.deleteSelectedCubeRelationshipsItem(cubeId);
      }
    });
  };

  @computed
  get dataSources() {
    return getTablesFromDataSetDistributions(this.dataSetDistributions).concat(
      getTablesFromDatasetUrl(this.cubeDetails[0])
    );
  }

  // SQL generation
  @computed
  get SQL() {
    let SQL = "";
    // WITH AS statments - Cube Links related
    if (this.selectedCubeLinks.length > 0 || this.isShowMetadataUsed) {
      SQL += "WITH ";
      SQL += [
        ...this.cubeLinkSQLWithAsStatements,
        ...this.variableMetadataWithAsStatements,
      ].join(",\n");
      SQL += "\n";
    }

    const primaryCubeDBAlias = sanitizeSqlAlias(this.cubeDetails[0].code);
    // SELECT
    SQL += "SELECT ";
    if (this.keepOrder.length === 0) {
      SQL += "*\n";
    } else {
      SQL += "\n  ";
      let selectedColumns = [];
      if (this.isGroupByUsed) {
        if (
          this.hasTemplateRepresentation &&
          this.selectedCubeLinks.length > 0
        ) {
          selectedColumns.push(`${primaryCubeDBAlias}.data_name`);
        }
      }
      for (const itemIndex of this.keepOrder) {
        let variableCode =
          this.cubeStructureItems[itemIndex].object.cubeVariableCode;

        let variableCodeMetadataName = `${variableCode}_name`;

        // DB Alias
        if (this.hasForeignCubes) {
          const cubeDetails = this.getCubeDetailsByItemKey(itemIndex);
          const databaseAlias = sanitizeSqlAlias(cubeDetails.code);
          variableCode = `${databaseAlias}.${variableCode}`;
        }

        // Aggr. fn. and Alias:
        // "GROUP BY" must be used, but not for the current item
        if (this.isGroupByUsed && this.keepState[itemIndex].groupBy === false) {
          // Aggregation function
          const aggregationFunction =
            this.keepState[itemIndex].aggregationFunction;
          if (aggregationFunction !== "") {
            variableCode = `${aggregationFunction}(${variableCode})`;
          }
        }

        // Alias
        const alias = this.keepState[itemIndex].alias;
        if (alias !== "") {
          variableCode = `${variableCode} AS '${sanitizeSqlAlias(alias)}'`;
          variableCodeMetadataName = `\`${sanitizeSqlAlias(alias)}_name\``;
        }

        if (this.keepState[itemIndex].showMetadata) {
          variableCode = `${variableCode}, ${variableCodeMetadataName}`;
        }

        selectedColumns.push(variableCode);
      }
      SQL += `${selectedColumns.join(",\n  ")} \n`;
    }

    // FROM
    if (this.selectedCubeLinks.length <= 0) {
      const databaseTable =
        this.dataSource === "NO_DATA_SOURCE"
          ? "/* please insert the data source */"
          : this.dataSource;
      SQL += `FROM ${databaseTable}${
        this.hasForeignCubes ? ` AS ${primaryCubeDBAlias}` : ""
      }`;
    } else {
      SQL += this.cubeLinkMainSQL;
    }

    // JOIN a.k.a. foreign cubes from Cube Relationships
    let join = "";
    const processedCubeIds = new Set([this.cubeDetails[0].cubeId]);
    for (const cubeId of this.selectedCubeRelationships) {
      const linkedVariables = this.cubeRelationships[cubeId].onConditions;
      const onConditions = linkedVariables
        .filter((lv) => processedCubeIds.has(lv.primaryCubeId))
        .map((lv) => {
          const foreignAlias = sanitizeSqlAlias(
            this.foreignCubes[lv.foreignCubeId].details.code
          );
          const linkedCubeId = this.cubeDetails[0].cubeId;
          const primaryAlias =
            lv.primaryCubeId === linkedCubeId
              ? primaryCubeDBAlias
              : sanitizeSqlAlias(
                  this.foreignCubes[lv.primaryCubeId].details.code
                );
          return `${primaryAlias}.${lv.primaryCubeVariableCode} = ${foreignAlias}.${lv.foreignCubeVariableCode}`;
        });

      const joinType = (() => {
        if (CUBE_RELATIONSHIPS_ONLY_USE_LEFT_JOIN) {
          return "LEFT JOIN";
        }

        /**
         * Use left join if the cube on the left is Mandatory but the foreign is not
         * Use right join if the primary Cube is not mandatory but the Foreign is
         * Use the inner if both are mandatory
         * Use the Full outer if none is.
         * Use left if some of the mandatoriness is null
         */
        const { primaryCubeMandatoriness, foreignCubeMandatoriness } =
          this.cubeRelationships[cubeId];
        if (
          primaryCubeMandatoriness === true &&
          foreignCubeMandatoriness === true
        ) {
          return "INNER JOIN";
        }
        if (
          primaryCubeMandatoriness === false &&
          foreignCubeMandatoriness === true
        ) {
          return "RIGHT JOIN";
        }
        if (
          primaryCubeMandatoriness === false &&
          foreignCubeMandatoriness === false
        ) {
          return "FULL OUTER JOIN";
        }
        // Otherwise:
        return "LEFT JOIN";
      })();

      const foreignAlias = sanitizeSqlAlias(
        this.foreignCubes[cubeId].details.code
      );
      const foreignDataSource =
        this.selectedForeignDataSources[cubeId] ||
        "/* please insert the data source */";
      join += `\n  ${joinType} ${foreignDataSource} AS ${foreignAlias}`;
      join += ` ON ${onConditions.join(" AND ")}`;

      processedCubeIds.add(cubeId);
    }
    SQL += join;

    // JOIN ENUMERATED VARIABLES METADATA TABLES
    SQL += this.joinForEnumeratedVariablesMetadataTables;

    // WHERE
    const whereStart = this.filterOperatorStart.trim();
    const whereEnd = this.filterOperatorEnd.trim();

    const membersMap = this.membersMap;
    const variableSetMap = this.variableSetMap;

    const containsSeriesKeysFilter =
      this.hasCombinations &&
      !this.hasTemplateRepresentation &&
      this.seriesKeysFilter;

    if (
      this.filterOrder.length > 0 ||
      Object.values(this.templateRepresentationCells).length > 0 ||
      this.hasTemplateRepresentation ||
      containsSeriesKeysFilter
    ) {
      SQL += `\nWHERE`;
    }
    const whereLines = this.filterOrder
      .filter((itemID) => {
        // Filter out fields based on business rules:
        const filterState = this.filterState[itemID];
        const { kind, itemIndex } = filterState;
        return !(
          this.hasTemplateRepresentation &&
          (kind === "enumeratedSubdomain" ||
            kind === "enumeratedVariableSet") &&
          !this.cubeStructureItems[itemIndex].object.isImplemented &&
          itemIndex.startsWith(`${this.cubeDetails[0].cubeId}.`)
        );
      })
      .map((itemID) => {
        const filterState = this.filterState[itemID];
        const { kind, values, itemIndex, equalityOption } = filterState;
        const cubeDetails = this.getCubeDetailsByItemKey(itemIndex);
        const cubeId = cubeDetails.cubeId;
        const databaseAlias = this.hasForeignCubes
          ? `${sanitizeSqlAlias(cubeDetails.code)}.`
          : "";
        const item = this.cubeStructureItems[itemIndex];
        const variableCode = item.object.cubeVariableCode;
        const variableId = item.object.variableId;

        switch (kind) {
          case "enumeratedSubdomain":
            return `CAST(${databaseAlias}${variableCode} AS STRING) IN (${values
              .map((v) => membersMap[cubeId][variableId][v])
              .filter((member) => member !== undefined)
              .map((member) => {
                let containsNull = false;
                if (notApplicableItems.includes(member.name)) {
                  containsNull = true;
                }
                return `'${member?.code}'${containsNull ? ", NULL" : ""}`;
              }) // add single quotes
              .join(", ")})`;
          case "enumeratedVariableSet":
            return `CAST(${databaseAlias}${variableCode} AS STRING) IN (${values
              .map((v) => variableSetMap[variableId][v])
              .filter((variable) => variable !== undefined)
              .map((variable) => {
                let containsNull = false;
                if (notApplicableItems.includes(variable.name)) {
                  containsNull = true;
                }
                return `'${variable.code}'${containsNull ? ", NULL" : ""}`;
              }) // add single quotes
              .join(", ")})`;
          case "date-range":
            const dateFormat = getDateFormat(item);
            if (equalityOption === "Equals") {
              return `CAST(${databaseAlias}${variableCode} AS STRING) = '${formatDate(
                values.singleDate,
                dateFormat
              )}'${
                dateFormat ? "" : " /* format not defined, using YYYY-MM-DD */"
              }`;
            } else {
              if (values.from && !values.to) {
                return `CAST(${databaseAlias}${variableCode} AS STRING) >= '${formatDate(
                  values.from,
                  dateFormat
                )}'${
                  dateFormat
                    ? ""
                    : " /* format not defined, using YYYY-MM-DD */"
                }`;
              } else if (!values.from && values.to) {
                return `CAST(${databaseAlias}${variableCode} AS STRING) <= '${formatDate(
                  values.to,
                  dateFormat
                )}'${
                  dateFormat
                    ? ""
                    : " /* format not defined, using YYYY-MM-DD */"
                }`;
              } else {
                return `CAST(${databaseAlias}${variableCode} AS STRING) BETWEEN '${formatDate(
                  values.from,
                  dateFormat
                )}' AND '${formatDate(values.to, dateFormat)}'${
                  dateFormat
                    ? ""
                    : " /* format not defined, using YYYY-MM-DD */"
                }`;
              }
            }
          case "string":
            if (equalityOption === "Equals") {
              const query = values
                .split(";")
                .map((inputValue) => `'${inputValue.trim()}'`)
                .join(", ");
              return `${databaseAlias}${variableCode} in (${query})`;
            } else {
              return `${databaseAlias}${variableCode} LIKE '%${values}%'`;
            }
          case "number-range":
            if (equalityOption === "Equals") {
              const query = values.numbers
                ? values.numbers
                    .split(";")
                    .map((inputValue) => parseInt(inputValue.trim()))
                    .filter((num) => Number.isInteger(num))
                    .join(", ")
                : "";
              return `${databaseAlias}${variableCode} IN (${query})`;
            } else {
              if (values.from && !values.to) {
                return `${databaseAlias}${variableCode} >= ${values.from}`;
              } else if (!values.from && values.to) {
                return `${databaseAlias}${variableCode} <= ${values.to}`;
              } else {
                return `${databaseAlias}${variableCode} BETWEEN ${values.from} AND ${values.to}`;
              }
            }
          default:
            if (values.from && !values.to) {
              return `${databaseAlias}${variableCode} >= ${values.from}`;
            } else if (!values.from && values.to) {
              return `${databaseAlias}${variableCode} <= ${values.to}`;
            } else {
              return `${databaseAlias}${variableCode} BETWEEN ${values.from} AND ${values.to}`;
            }
        }
      });
    if (whereLines.length > 1) {
      if (whereStart) {
        SQL += `\n  ${whereStart}`;
      }
    }
    if (whereLines.length > 0) {
      SQL += `\n  ${whereLines[0]}`;
      for (let i = 1; i < whereLines.length; i++) {
        const operator = this.filterOperators[i];
        SQL += `\n  ${operator} ${whereLines[i]}`;
      }
    }
    if (whereLines.length > 1) {
      if (whereEnd) {
        SQL += `\n  ${whereEnd}`;
      }
    }

    // WHERE: Filter by Series Keys
    if (containsSeriesKeysFilter) {
      SQL += `\n  ${whereLines.length > 0 ? "AND " : ""}${
        this.hasForeignCubes ? `${primaryCubeDBAlias}.` : ""
      }series_key LIKE '%${
        this.seriesKeysFilter.split("*").join("%") // replaceAll not supported everywhere
      }%'`;
    }

    let tableId = this.cubeDetails[0].code;
    if (
      this.mappedNonReferenceCubes &&
      this.mappedNonReferenceCubes.length > 0
    ) {
      tableId = this.mappedNonReferenceCubes[0].code;
    }

    let whereTRItems = [];
    const dataNameCubeCode = sanitizeForDataName(tableId);
    for (let [i, indexValues] of Object.entries(
      this.templateRepresentationCells
    )) {
      for (const [z, yAndX] of Object.entries(indexValues)) {
        for (const [y, onlyX] of Object.entries(yAndX)) {
          for (const [x, xValue] of Object.entries(onlyX)) {
            // e.g.: data_name in (‘F_0101_s010_r0020_c0010’,’F_0101_s010_r020_c020’)
            // Important: prefixing x and y with "0" - may change in future.
            if (
              xValue &&
              this.isCombinationIdAllowed(
                this.cubeXYZ[i].byOrdinates[z][y][x].combinationId
              )
            ) {
              if (this.hasMembersInZAxis) {
                // if (this.selectAllForZAxis) {
                //   const zCode = "%"; // SQL wildcard instead of code
                //   whereTRItems.push({
                //     systemDataCode: `${dataNameCubeCode}_${
                //       zCode ? `s${zCode}_` : ""
                //     }${this.hasOpenYAxis || !y ? "" : `r${padWithZeros(y)}_`}${
                //       x ? `c${padWithZeros(x)}` : ""
                //     }`,
                //     tableCode: this.cubeXYZ[i].byOrdinates[z][y][x].tableCode,
                //   });
                // }
                // else {
                const zCodes = this.zAxisMemberCodes;
                if (
                  this.cubeXYZ[i].byOrdinates[z][y][x].systemDataCode &&
                  this.cubeXYZ[i].byOrdinates[z][y][x].tableCode
                ) {
                  whereTRItems.push({
                    systemDataCode:
                      this.cubeXYZ[i].byOrdinates[z][y][x].systemDataCode,
                    tableCode: this.cubeXYZ[i].byOrdinates[z][y][x].tableCode,
                  });
                } else {
                  for (const zCode of zCodes) {
                    whereTRItems.push({
                      systemDataCode: `${dataNameCubeCode}_${
                        zCode ? `s${zCode}_` : ""
                      }${
                        this.hasOpenYAxis || !y ? "" : `r${padWithZeros(y)}_`
                      }${x ? `c${padWithZeros(x)}` : ""}`,
                      tableCode: this.cubeXYZ[i].byOrdinates[z][y][x].tableCode,
                    });
                  }
                }
                // }
              } else {
                if (
                  this.cubeXYZ[i].byOrdinates[z][y][x].systemDataCode &&
                  this.cubeXYZ[i].byOrdinates[z][y][x].tableCode
                ) {
                  whereTRItems.push({
                    systemDataCode:
                      this.cubeXYZ[i].byOrdinates[z][y][x].systemDataCode,
                    tableCode: this.cubeXYZ[i].byOrdinates[z][y][x].tableCode,
                  });
                } else {
                  whereTRItems.push({
                    systemDataCode: `${dataNameCubeCode}_${z ? `s${z}_` : ""}${
                      this.hasOpenYAxis || !y ? "" : `r${padWithZeros(y)}_`
                    }${x ? `c${padWithZeros(x)}` : ""}`,
                    tableCode: this.cubeXYZ[i].byOrdinates[z][y][x].tableCode,
                  });
                }
              }
            }
          }
        }
      }
    }
    if (Object.values(this.templateRepresentationCells).length > 0) {
      if (whereTRItems[0]) {
        this.SetWhereTRItemesContainItems(true);
        const dataName = `${
          this.hasForeignCubes ? `${primaryCubeDBAlias}.` : ""
        }data_name`;
        // if (this.selectAllForZAxis) {
        //   SQL += `\n  ${
        //     whereLines.length > 0 ? "AND " : ""
        //   }(\n    ${dataName} LIKE '${whereTRItems.join(
        //     `'\n    OR ${dataName} LIKE '`
        //   )}'\n  )`;
        // } else {
        SQL += `\n  ${whereLines.length > 0 ? "AND " : ""}`;
        let byTableCode = whereTRItems.reduce(function (r, a) {
          r[a.tableCode] = r[a.tableCode] || [];
          r[a.tableCode].push(a);
          return r;
        }, Object.create(null));
        let byTableCodeIndex = 0;
        if (Object.entries(byTableCode).length > 1) SQL += `(`;
        for (const [z, yAndX] of Object.entries(byTableCode)) {
          if (z !== "undefined") {
            let systemDataCodes = yAndX.map((item) => item.systemDataCode);
            SQL += ` ${dataName} IN ('${systemDataCodes.join("', '")}') `;
            // SQL += `AND TABLE_ID="${z}"`;
            byTableCodeIndex += 1;
            if (Object.entries(byTableCode).length !== byTableCodeIndex)
              SQL += ` OR\n`;
            else if (
              Object.entries(byTableCode).length > 1 &&
              Object.entries(byTableCode).length === byTableCodeIndex
            )
              SQL += `)\n`;
          }
        }
        // }
        if (this.cubeDetails[0].filters !== null)
          SQL += ` AND ${this.cubeDetails[0].filters}`;
      } else {
        this.SetWhereTRItemesContainItems(false);
      }
    }

    if (
      this.hasTemplateRepresentation &&
      !this.getwhereTRItemsContainsItems()
    ) {
      SQL += ` /*no data points selected, please select data points to finalise the query */`;
    }

    // GROUP BY
    if (this.isGroupByUsed) {
      let selectedColumns = [];
      if (this.hasTemplateRepresentation && this.selectedCubeLinks.length > 0) {
        selectedColumns.push(`${primaryCubeDBAlias}.data_name`);
      }
      if (
        this.hasCombinations &&
        !this.hasTemplateRepresentation &&
        this.selectedCubeLinks.length > 0
      ) {
        selectedColumns.push(`${primaryCubeDBAlias}.series_key`);
      }
      for (const itemIndex of this.keepOrder) {
        if (this.keepState[itemIndex].groupBy) {
          let variableCode =
            this.cubeStructureItems[itemIndex].object.cubeVariableCode;
          let variableCodeMetadataName = `${variableCode}_name`;
          // DB Alias
          if (this.hasForeignCubes) {
            const cubeDetails = this.getCubeDetailsByItemKey(itemIndex);
            const databaseAlias = sanitizeSqlAlias(cubeDetails.code);
            variableCode = `${databaseAlias}.${variableCode}`;
          }

          // Alias
          const alias = this.keepState[itemIndex].alias;
          if (alias !== "") {
            variableCode = `\`${sanitizeSqlAlias(alias)}\``;
            variableCodeMetadataName = `\`${sanitizeSqlAlias(alias)}_name\``;
          }

          if (this.keepState[itemIndex].showMetadata) {
            variableCode = `${variableCode}, ${variableCodeMetadataName}`;
          }

          selectedColumns.push(variableCode);
        }
      }
      SQL += "\nGROUP BY\n  ";
      SQL += `${selectedColumns.join(",\n  ")}`;
    }

    // LIMIT
    if (`${this.limit}`.trim() !== "") {
      SQL += `\nLIMIT ${this.limit}`;
    }
    return SQL;
  }

  @computed
  get enumeratedVariableCodesWithShowMetadata() {
    const result = this.keepOrder
      .filter((itemKey) => {
        const showMetadata = this.keepState[itemKey].showMetadata;
        return showMetadata && this.isEnumerated(itemKey);
      })
      .map((itemKey) => ({
        variableCode: this.cubeStructureItems[itemKey].object.cubeVariableCode,
        alias: this.keepState[itemKey].alias,
        cubeCode: this.getCubeDetailsByItemKey(itemKey).code,
      }));
    return result;
  }

  @computed
  get variableMetadataWithAsStatements() {
    let withAsStatements = [];

    if (this.keepOrder.length === 0) {
      return "";
    } else {
      for (const { cubeCode, variableCode, alias } of this
        .enumeratedVariableCodesWithShowMetadata) {
        const aliasName =
          alias === ""
            ? `'${sanitizeSqlAlias(variableCode)}_name'`
            : `'${sanitizeSqlAlias(alias)}_name'`;
        let statement = "";
        statement += `${variableCode}_members AS\n`;
        statement += `(\n`;
        statement += `  SELECT distinct attribute_allowed_value_code, attribute_allowed_value_name AS ${aliasName}\n`;
        statement += `  FROM crp_sdd.sdd_entity_essential\n`;
        statement += `  WHERE entity_code='${cubeCode}' AND attribute_code=UPPER('${variableCode}')\n`;
        statement += `)\n`;

        withAsStatements.push(statement);
      }
    }
    return withAsStatements;
  }

  @computed
  get joinForEnumeratedVariablesMetadataTables() {
    let joins = [];
    if (this.enumeratedVariableCodesWithShowMetadata.length === 0) {
      return "";
    } else {
      for (const { variableCode } of this
        .enumeratedVariableCodesWithShowMetadata) {
        let statement = "";
        statement += `LEFT JOIN ${variableCode}_members ON CAST(${variableCode} AS VARCHAR(10)) = ${variableCode}_members.attribute_allowed_value_code`;
        joins.push(statement);
      }
    }
    return "\n  " + joins.join("\n  ");
  }

  /**
   * Part of SQL that returns Cube Link specific SQL statements having "WITH something AS" at the beginning.
   */
  @computed
  get cubeLinkSQLWithAsStatements() {
    if (this.selectedCubeLinks.length <= 0) {
      return [];
    }

    const primaryCubeCode = sanitizeSqlAlias(this.cubeDetails[0].code);
    const primaryDatabaseTable =
      this.dataSource === "NO_DATA_SOURCE"
        ? "/* please insert the data source */"
        : this.dataSource;

    // "WITH AS" for primary cube

    let primaryWithAs = "";
    primaryWithAs += `FILTERED_${primaryCubeCode} AS\n`;
    primaryWithAs += `(\n`;
    primaryWithAs += `  SELECT *\n`;
    primaryWithAs += `  FROM ${primaryDatabaseTable}\n`;

    let incrementalLetter = "a";
    for (const cubeId of this.selectedCubeLinks) {
      const cubeStructureItemLinks =
        this.cubeStructureItemLinksByCubeId[cubeId];
      const cubeStructureItemLinksHavingMemberLinks =
        cubeStructureItemLinks.filter(
          ({ memberLinks }) => memberLinks.length > 0
        );

      for (const cubeStructureItemLink of cubeStructureItemLinksHavingMemberLinks) {
        primaryWithAs += `  LEFT JOIN \n`;
        primaryWithAs += `  (\n`;
        primaryWithAs += `    SELECT DISTINCT mem.code AS ${
          cubeStructureItemLink.primaryCubeVariableCode
        }_primary_code, mem_link.primary_member_id AS ${sanitizeSqlAlias(
          cubeStructureItemLink.primaryCubeVariableCode
        )}_primary_mapped\n`;
        primaryWithAs += `    FROM crp_sdd.sdd_member_link as mem_link\n`;
        primaryWithAs += `    LEFT JOIN crp_sdd.sdd_member as mem\n`;
        primaryWithAs += `    ON mem_link.primary_member_id=mem.member_id\n`;
        primaryWithAs += `    WHERE mem_link.cube_structure_item_link_id='${cubeStructureItemLink.cubeStructureItemLinkId}'`;
        // primaryWithAs += ` AND mem_link.is_linked='1'\n`; // temporarily commented, because the field is not implemented in DISC yet
        primaryWithAs += `  ) ${incrementalLetter}\n`;
        primaryWithAs += `  ON cast(${cubeStructureItemLink.primaryCubeVariableCode} AS varchar(20))=${incrementalLetter}.${cubeStructureItemLink.primaryCubeVariableCode}_primary_code\n`;

        // a -> b -> c ... etc
        incrementalLetter = String.fromCharCode(
          incrementalLetter.charCodeAt(0) + 1
        );
      }
    }
    primaryWithAs += `  WHERE\n`;
    const primaryWhereParts = [];
    for (const cubeId of this.selectedCubeLinks) {
      const cubeStructureItemLinks =
        this.cubeStructureItemLinksByCubeId[cubeId];
      const cubeStructureItemLinksHavingMemberLinks =
        cubeStructureItemLinks.filter(
          ({ memberLinks }) => memberLinks.length > 0
        );

      for (const cubeStructureItemLink of cubeStructureItemLinksHavingMemberLinks) {
        primaryWhereParts.push(
          `${sanitizeSqlAlias(
            cubeStructureItemLink.primaryCubeVariableCode
          )}_primary_mapped IS NOT NULL`
        );
      }
    }
    primaryWithAs += `    ${primaryWhereParts.join(" AND ")}\n`;
    primaryWithAs += `)`;

    // "WITH AS" for foreign cubes
    let foreignWithAsStatements = [];
    for (const cubeId of this.selectedCubeLinks) {
      let foreignWithAs = "";
      const foreignCubeCode = sanitizeSqlAlias(
        this.foreignCubes[cubeId].details.code
      );
      const foreignDataSource =
        this.selectedForeignDataSources[cubeId] ||
        "/* please insert the data source */";
      const cubeStructureItemLinks =
        this.cubeStructureItemLinksByCubeId[cubeId];
      const cubeStructureItemLinksHavingMemberLinks =
        cubeStructureItemLinks.filter(
          ({ memberLinks }) => memberLinks.length > 0
        );

      foreignWithAs += `FILTERED_${foreignCubeCode} as \n`;
      foreignWithAs += `(\n`;
      foreignWithAs += `  SELECT *\n`;
      foreignWithAs += `  FROM ${foreignDataSource}\n`;

      let incrementalLetter = "a";

      for (const cubeStructureItemLink of cubeStructureItemLinksHavingMemberLinks) {
        foreignWithAs += `    LEFT JOIN \n`;
        foreignWithAs += `    (\n`;
        foreignWithAs += `      SELECT DISTINCT mem.code AS ${
          cubeStructureItemLink.foreignCubeVariableCode
        }_foreign_code, mem_link.primary_member_id AS ${sanitizeSqlAlias(
          cubeStructureItemLink.foreignCubeVariableCode
        )}_foreign_mapped\n`;
        foreignWithAs += `      FROM crp_sdd.sdd_member_link as mem_link\n`;
        foreignWithAs += `      LEFT JOIN crp_sdd.sdd_member as mem\n`;
        foreignWithAs += `      ON mem_link.foreign_member_id=mem.member_id\n`;
        foreignWithAs += `      WHERE mem_link.cube_structure_item_link_id='${cubeStructureItemLink.cubeStructureItemLinkId}'`;
        // foreignWithAs += ` AND mem_link.is_linked='1'\n`; // temporarily commented, because the field is not implemented in DISC yet
        foreignWithAs += `    ) ${incrementalLetter}\n`;
        foreignWithAs += `    ON cast(${cubeStructureItemLink.foreignCubeVariableCode} AS varchar(20))=${incrementalLetter}.${cubeStructureItemLink.foreignCubeVariableCode}_foreign_code\n`;

        // a -> b -> c ... etc
        incrementalLetter = String.fromCharCode(
          incrementalLetter.charCodeAt(0) + 1
        );
      }
      foreignWithAs += `  WHERE\n`;
      let foreignWhereParts = [];
      for (const cubeStructureItemLink of cubeStructureItemLinksHavingMemberLinks) {
        foreignWhereParts.push(
          `${sanitizeSqlAlias(
            cubeStructureItemLink.foreignCubeVariableCode
          )}_foreign_mapped IS NOT NULL`
        );
      }
      foreignWithAs += `    ${foreignWhereParts.join(" AND ")}\n`;
      foreignWithAs += `)`;
      foreignWithAsStatements.push(foreignWithAs);
    }

    return [primaryWithAs, ...foreignWithAsStatements];
  }

  @computed
  get cubeLinkMainSQL() {
    const primaryCubeCode = sanitizeSqlAlias(this.cubeDetails[0].code);
    let mainSQL = "";
    mainSQL += `FROM FILTERED_${primaryCubeCode} AS ${primaryCubeCode}\n`;

    for (const cubeId of this.selectedCubeLinks) {
      const foreignCubeCode = sanitizeSqlAlias(
        this.foreignCubes[cubeId].details.code
      );
      const cubeStructureItemLinks =
        this.cubeStructureItemLinksByCubeId[cubeId];
      mainSQL += `LEFT JOIN FILTERED_${foreignCubeCode} AS ${foreignCubeCode}\n`;
      mainSQL += `ON \n`;
      const onConditions = cubeStructureItemLinks.map(
        ({ foreignCubeVariableCode, primaryCubeVariableCode, memberLinks }) => {
          const isEnum = memberLinks && memberLinks.length > 0;
          return `  ${foreignCubeCode}.${foreignCubeVariableCode}${
            isEnum ? "_foreign_mapped" : ""
          }=${primaryCubeCode}.${primaryCubeVariableCode}${
            isEnum ? "_primary_mapped" : ""
          }`;
        }
      );
      mainSQL += onConditions.join(" AND\n");
    }

    return mainSQL;
  }

  @computed
  get getOpenFromCubeLinkPage() {
    return this.openFromCubeLinkPage;
  }
}
